import type { LayerStats } from '@/components/maplibre/types';

/**
 * GEO-589 display-name resolution.
 *
 * On upload the worker sanitizes column names into safe identifiers (the
 * canonical `column_name` used by tilesets, style expressions and the EAV
 * tables) and preserves the original label as `display_name`. The original
 * label is written to two places:
 *   - `layer.stats.fields[].display_name` for EVERY uploaded layer, and
 *   - `layer_columns.display_name`, but only for layers that actually
 *     materialize `layer_columns` rows (and not reliably even then).
 *
 * `stats.fields` is therefore the single source that is always populated, so
 * the UI resolves a label with the precedence:
 *   `layer_columns.display_name` ?? stats-derived original ?? null
 * Callers fall back to the sanitized `column_name` when the result is null.
 */

/**
 * Builds a `{ sanitizedColumnName -> originalDisplayName }` lookup from a
 * layer's `stats` blob. Only entries that actually carry a `display_name`
 * (i.e. columns the worker renamed) are included.
 */
export function buildStatsDisplayNameMap(stats: unknown): Record<string, string> {
  const map: Record<string, string> = {};
  if (stats && typeof stats === 'object' && 'fields' in stats) {
    const fields = (stats as LayerStats).fields;
    if (Array.isArray(fields)) {
      for (const field of fields) {
        if (field?.name && field.display_name) {
          map[field.name] = field.display_name;
        }
      }
    }
  }
  return map;
}

/**
 * Resolves the display label for a column. An explicit `layer_columns`
 * display_name wins; otherwise fall back to the stats-derived original label;
 * otherwise null so the caller renders the sanitized `column_name`.
 */
export function resolveColumnDisplayName(
  columnName: string,
  columnDisplayName: string | null | undefined,
  statsDisplayNames: Record<string, string>
): string | null {
  return columnDisplayName ?? statsDisplayNames[columnName] ?? null;
}
